/**
 * TypeScript types for DataChannel messages in visio rooms.
 * Used by useBreakout.ts, VisioRoom.tsx, and any future DataChannel consumers.
 */

// -- Breakout messages --------------------------------------------------------

export interface BreakoutAssignMessage {
  type: 'breakout_assign'
  tokens: Record<string, string>
  url: string
  assignments: Record<string, number>
  timer_minutes?: number
  started_at?: number
}

export interface BreakoutRecallMessage {
  type: 'breakout_recall'
  tokens: Record<string, string>
  url: string
}

// -- Admin control messages ---------------------------------------------------

export interface AdminMuteMessage {
  type: 'admin_mute'
  track: 'audio' | 'video'
}

// -- Whiteboard messages ------------------------------------------------------

export interface WhiteboardOnMessage {
  type: 'whiteboard_on'
}

export interface WhiteboardOffMessage {
  type: 'whiteboard_off'
}

// -- Annotation messages ------------------------------------------------------

export interface AnnotationStrokeMessage {
  type: 'annotation'
  action: 'stroke'
  data: {
    id: string
    tool: string
    points: { x: number; y: number }[]
    color: string
    width: number
    text?: string
    author: string
  }
}

export interface AnnotationUndoMessage {
  type: 'annotation'
  action: 'undo'
  data: { id: string }
}

export interface AnnotationEraseMessage {
  type: 'annotation'
  action: 'erase'
  data: { id: string }
}

export interface AnnotationClearMessage {
  type: 'annotation'
  action: 'clear'
}

export interface AnnotationToggleMessage {
  type: 'annotation'
  action: 'toggle'
  enabled: boolean
}

// -- Caption messages ---------------------------------------------------------

export interface CaptionMessage {
  type: 'caption'
  from: string       // participant identity
  text: string
  lang: string
  final: boolean
}

// -- Union type ---------------------------------------------------------------

export type DataMessage =
  | BreakoutAssignMessage
  | BreakoutRecallMessage
  | AdminMuteMessage
  | WhiteboardOnMessage
  | WhiteboardOffMessage
  | AnnotationStrokeMessage
  | AnnotationUndoMessage
  | AnnotationEraseMessage
  | AnnotationClearMessage
  | AnnotationToggleMessage
  | CaptionMessage

// -- Helper -------------------------------------------------------------------

const decoder = new TextDecoder()

export function parseDataMessage(payload: Uint8Array): DataMessage | null {
  try {
    return JSON.parse(decoder.decode(payload)) as DataMessage
  } catch {
    return null
  }
}
